import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  GettingStartedModalState,
  OneTimeModal,
  OneTimeModalLookupArgs,
  OneTimeModalLookupResult,
  UserHasSeenResource,
} from './oneTimeModal.types';
import { baseQueryWithAuth } from '../baseQuery';

export const oneTimeModalApi = createApi({
  reducerPath: 'oneTimeModalApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['CurrentModal', 'ModalLookup', 'GettingStartedModal'],
  endpoints: (build) => ({
    getCurrentOneTimeModal: build.query<OneTimeModal | null, undefined>({
      query: () => 'users/one-time-modal/current',
      providesTags: ['CurrentModal'],
    }),
    lookupModal: build.query<OneTimeModalLookupResult, OneTimeModalLookupArgs>({
      query: ({ kind, pageKey }) => {
        const params = new URLSearchParams({ kind, page_key: pageKey });

        return `users/one-time-modal/lookup?${params}`;
      },
      providesTags: (_result, _error, { kind, pageKey }) => [
        { type: 'ModalLookup', id: `${kind}:${pageKey}` },
      ],
    }),
    getGettingStartedModalState: build.query<GettingStartedModalState, undefined>({
      query: () => 'users/getting-started-modal/state',
      providesTags: ['GettingStartedModal'],
    }),
    markOneTimeModalSeen: build.mutation<UserHasSeenResource, number>({
      query: (modalId) => ({
        url: `users/one-time-modal/${modalId}/seen`,
        method: 'POST',
      }),
      // Optimistically flip to seen=true
      async onQueryStarted(modalId, { dispatch, getState, queryFulfilled }) {
        const argsList = oneTimeModalApi.util.selectCachedArgsForQuery(getState(), 'lookupModal');
        const patches = argsList.map((args) =>
          dispatch(
            oneTimeModalApi.util.updateQueryData('lookupModal', args, (draft) => {
              if (draft && draft.modal.id === modalId) {
                return { ...draft, seen: true };
              }
            }),
          ),
        );

        try {
          await queryFulfilled;
        } catch (error) {
          patches.forEach((p) => p.undo());
          console.error('Failed to mark one-time modal seen:', modalId, error);
        }
      },
      invalidatesTags: ['CurrentModal', 'ModalLookup'],
    }),
    markGettingStartedModalSeen: build.mutation<UserHasSeenResource, undefined>({
      query: () => ({
        url: 'users/getting-started-modal/seen',
        method: 'POST',
      }),
      invalidatesTags: ['GettingStartedModal'],
    }),
  }),
});

export const {
  useGetCurrentOneTimeModalQuery,
  useLookupModalQuery,
  useGetGettingStartedModalStateQuery,
  useMarkOneTimeModalSeenMutation,
  useMarkGettingStartedModalSeenMutation,
} = oneTimeModalApi;
